import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  Observable,
  split,
  type ServerError,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { createClient } from 'graphql-ws';

import { datadogRum } from '@datadog/browser-rum';
import { getAuth } from './contexts/auth-context';
import {
  getGraphqlErrorCode,
  getGraphqlErrorRawCode,
  graphqlErrorsFrom,
  type ErrorHandlingMode,
} from './utils/graphql-error-codes';
import { notify } from './utils/notification-bridge';
import { newRequestId } from './utils/request-id';

// App-raised codes (mirrors core/graphql/error_reporting.py): log as Datadog actions,
// not errors — expected, shouldn't alert.
const APP_RAISED_CODES: ReadonlySet<string> = new Set([
  'BAD_USER_INPUT',
  'CONFLICT',
  'NOT_FOUND',
  'FORBIDDEN',
  'AUTH_REQUIRED',
]);

// Dedupe against a burst of AUTH_REQUIRED errors causing a redirect loop.
let authRedirectInProgress = false;

const redirectToSignIn = (action: string, extra: Record<string, unknown> = {}) => {
  if (authRedirectInProgress) return;
  // No token → no session to recover; a hard nav to /sign-in would just be a pointless reload.
  if (!getAuth().token) return;
  authRedirectInProgress = true;
  datadogRum.addAction(action, extra);
  localStorage.removeItem('auth');
  window.location.href = '/sign-in';
};

// Public auth/onboarding screens: nothing to eject from, and AUTH_REQUIRED is expected here
// (e.g. `me` for a just-registered, unconfirmed user) — so don't redirect to /sign-in.
const PUBLIC_AUTH_PATH_PREFIXES = [
  '/auth',
  '/sign-in',
  '/sign-up',
  '/confirm-registration',
  '/forgot-password',
  '/reset-password',
  '/unsubscribe',
  '/subscribed-to-news',
];

const isOnPublicAuthRoute = (): boolean =>
  PUBLIC_AUTH_PATH_PREFIXES.some((prefix) => window.location.pathname.startsWith(prefix));

const authLink = new ApolloLink((operation, forward) => {
  const { token } = getAuth();
  if (token) {
    try {
      const tokenExpiration = JSON.parse(atob(token.split('.')[1])).exp * 1000;
      const currentTime = Date.now();
      if (tokenExpiration < currentTime) {
        redirectToSignIn('USER_TOKEN_EXPIRED', {
          tokenExpiration,
          currentTime,
          operationName: operation.operationName,
        });
      }
    } catch (error) {
      console.error('Error decoding token:', error);
    }
  } else {
    datadogRum.addAction('USER_TOKEN_ABSENT', {
      token,
      operationName: operation.operationName,
    });
  }
  // Correlation ID: one per operation, read back by errorLink/snackbar.
  const requestId = newRequestId();
  operation.setContext({ requestId });
  operation.setContext(({ headers }: { headers: Record<string, string> }) => ({
    headers: {
      ...headers,
      authorization: token ? `JWT ${token}` : '',
    },
  }));

  return forward(operation);
});

const wsClient = createClient({
  url: import.meta.env.VITE_SOCKET_URL || '',
  lazy: true,
  retryAttempts: 3,
  connectionParams: () => {
    const { token } = getAuth();
    return { token };
  },
});

const wsLink = new GraphQLWsLink(wsClient);

const httpLink = createUploadLink({
  fetch,
  uri: `${import.meta.env.VITE_BACKEND_URL}/graphql/`,
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  const definition = getMainDefinition(operation.query);
  const operationType = definition.kind === 'OperationDefinition' ? definition.operation : 'query';
  // Default: queries notify (else the silent data:null bug hides failures); mutations/subscriptions
  // silent (call site owns its UX). Override via `context: { errorHandling }`.
  const ctxMode = operation.getContext().errorHandling as ErrorHandlingMode | undefined;
  const mode: ErrorHandlingMode = ctxMode ?? (operationType === 'query' ? 'notify' : 'silent');
  const requestId = operation.getContext().requestId as string | undefined;
  const graphqlErrors = graphqlErrorsFrom(graphQLErrors, networkError);

  // Telemetry always fires, regardless of `mode`. Per-error code so multi-error responses stay
  // searchable; app-raised codes → addAction (no alert), rest → addError.
  if (graphqlErrors.length) {
    graphqlErrors.forEach((err) => {
      const errCode = typeof err.extensions?.code === 'string' ? err.extensions.code : undefined;
      const payload = {
        locations: err.locations,
        path: err.path,
        code: errCode,
        operationType,
        operationName: operation.operationName,
        requestId,
      };
      if (errCode && APP_RAISED_CODES.has(errCode)) {
        datadogRum.addAction(`graphql.app_error.${errCode}`, {
          message: err.message ?? 'Unknown GraphQL error',
          ...payload,
        });
      } else {
        datadogRum.addError(`GraphQL error: ${err.message ?? 'Unknown GraphQL error'}`, payload);
      }
    });
  }
  if (networkError && graphqlErrors.length === 0) {
    const errorMessage = `[Network error]: ${networkError.message || 'Unknown network error'}`;
    const detailedError = networkError instanceof Error ? networkError : new Error(errorMessage);
    datadogRum.addError(detailedError, {
      message: errorMessage,
      stack: networkError.stack || 'No stack available',
      code: getGraphqlErrorRawCode(graphQLErrors, networkError),
      operationType,
      operationName: operation.operationName,
      networkError,
      requestId,
    });
  }

  const code = getGraphqlErrorCode(graphQLErrors, networkError);

  // Single operation-aware dev log.
  if (import.meta.env.DEV) {
    const label = `[GraphQL ${operationType}] ${operation.operationName ?? '<anonymous>'}`;
    if (graphqlErrors.length) {
      console.error(label, { code, graphQLErrors: graphqlErrors });
    } else if (networkError) {
      console.error(label, { code, networkError });
    }
  }

  // AUTH_REQUIRED: session handling, not error UX — redirect regardless of `errorHandling` mode,
  // except on public auth routes (see above).
  if (code === 'AUTH_REQUIRED') {
    if (isOnPublicAuthRoute()) {
      datadogRum.addAction('AUTH_REQUIRED_ON_PUBLIC_ROUTE', {
        operationName: operation.operationName,
        pathname: window.location.pathname,
      });
    } else {
      redirectToSignIn('SERVER_REJECTED_AUTH', { operationName: operation.operationName });
    }
    return;
  }

  if (mode === 'silent') return;

  if (graphqlErrors.length > 0) {
    let message: string;
    let surfaceReferenceId = false;
    switch (code) {
      case 'NOT_FOUND':
        message = 'errors.graphql.notFound';
        break;
      case 'CONFLICT':
        message = 'errors.graphql.conflict';
        break;
      case 'FORBIDDEN':
        message = 'errors.graphql.forbidden';
        break;
      case 'BAD_USER_INPUT':
        // User-correctable; call sites usually handle field-level locally — fallback snackbar.
        message = 'errors.graphql.invalidInput';
        break;
      case 'GRAPHQL_VALIDATION_FAILED':
      case 'GRAPHQL_PARSE_FAILED':
        // Schema mismatch / malformed variable — client bug or version skew.
        message = 'errors.graphql.appOutdated';
        break;
      default:
        // INTERNAL_SERVER_ERROR / unrecognised. Surface the request ID for support tickets.
        message = 'errors.graphql.unexpected';
        surfaceReferenceId = true;
    }
    notify({ visibility: true, message, referenceId: surfaceReferenceId ? requestId : undefined });
    return;
  }

  if (networkError) {
    notify({ visibility: true, message: 'errors.graphql.network', referenceId: requestId });
  }
});

// Retry only transport failures + 5xx. 4xx won't change on retry and doubles BE load.
const retryLink = new RetryLink({
  attempts: {
    max: 1,
    retryIf: (error) => {
      if (!error) return false;
      const serverErr = error as ServerError;
      // No statusCode → no response (CORS/DNS/offline).
      if (serverErr.statusCode == null) return true;
      return serverErr.statusCode >= 500;
    },
  },
  delay: {
    initial: 300,
    max: 2000,
    jitter: true,
  },
});

// fetch never rejects a hung request — abort after a deadline so errorLink sees a networkError.
// Override via `context: { timeoutMs }`; defers to a caller-provided AbortSignal (Stripe checkout).
const DEFAULT_TIMEOUT_MS = 30_000;

const timeoutLink = new ApolloLink((operation, forward) => {
  const ctx = operation.getContext();
  if (ctx.fetchOptions?.signal) return forward(operation);

  const timeoutMs = (ctx.timeoutMs as number | undefined) ?? DEFAULT_TIMEOUT_MS;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  operation.setContext({
    fetchOptions: { ...(ctx.fetchOptions ?? {}), signal: controller.signal },
  });

  return new Observable((observer) => {
    const sub = forward(operation).subscribe({
      next: (v) => observer.next(v),
      error: (e) => {
        clearTimeout(timer);
        observer.error(e);
      },
      complete: () => {
        clearTimeout(timer);
        observer.complete();
      },
    });
    return () => {
      clearTimeout(timer);
      sub.unsubscribe();
    };
  });
});

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);

    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  // timeoutLink applies to HTTP only; subscriptions use wsLink's own retry.
  ApolloLink.from([timeoutLink, httpLink])
);
const client = new ApolloClient({
  link: ApolloLink.from([authLink, retryLink, errorLink, splitLink]),
  // errorPolicy: 'all' is load-bearing: surfaces data + error together so errorLink notifies
  // while the call site renders partial data. 'none' reintroduces the silent data:null failures.
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
    },
    query: {
      errorPolicy: 'all',
    },
  },
  cache: new InMemoryCache({
    typePolicies: {
      OngoingUserLearningSessionType: {
        keyFields: ['learningSession', ['id']],
      },
    },
  }),
});

export default client;
